import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Card } from '@ui/themed';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { PlanUpgradePrompt } from '../../../components/plan-upgrade-prompt';
import { SettingActionRow } from '../../../components/setting-action-row';
import { SettingToggleRow } from '../../../components/setting-toggle-row';
import {
  featureEnabled,
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../constants/features';
import { INTERCOM_TRIGGER } from '../../../constants/intercom';
import { useEnv } from '../../../context/env-context';
import { useUser } from '../../../context/page-context';
import type { Setting } from '../../../context/settings-context';
import { useSettings, useUpdateSettings } from '../../../context/settings-context';
import { DOCS_URLS, I18N_PAGE } from '../constants';
import { useSettingToggle } from '../hooks/use-setting-toggle';
import type { ConfigurationsModal } from '../types';
import { SettingRow } from './setting-row';

const FIELDS = `${I18N_PAGE}.cards.features.fields` as const;
const COMPONENTS = 'features:configurations.components';

export function FeaturesCard({
  openVueModal,
}: {
  openVueModal: (modal: ConfigurationsModal) => void;
}) {
  const env = useEnv();
  const { isSparkStaff } = useUser();
  const settings = useSettings();
  const { mutate } = useUpdateSettings();

  const quotingEnabled = settings.quotingEnabled ?? false;
  const purchaseTimelineEnabled = settings.purchaseTimelineEnabled ?? false;
  const invoicingEnabled = settings.invoicingEnabled ?? false;
  const quotingFeatureEnabled = featureEnabled(features.QUOTING, env.team.slugged_name);
  const paymentsVisibilityRow =
    !!settings.useCustomersAPI &&
    featureEnabled(features.PAYMENTS_VISIBILITY, env.team.slugged_name);

  const companyUsers = useSettingToggle('subAccountsEnabled', false);
  const purchaseTimeline = useSettingToggle('purchaseTimelineEnabled', false);
  const invoicing = useSettingToggle('invoicingEnabled', false);
  const multiLocationStock = useSettingToggle('useMultiLocationStock', false);
  const paymentsVisibility = useSettingToggle('paymentVisibilityEnabled', false);

  const quotingUpsell = !env.billing.is_advanced_features_enabled;
  const invoicingRestricted = featureRestricted(
    features.INVOICING,
    getActiveBillingPlanKey(env.billing.plan_key),
  );
  const notificationsActive = quotingEnabled || purchaseTimelineEnabled;

  const quotingToggle = {
    checked: quotingEnabled,
    onChange: (value: boolean) => {
      if (value) {
        openVueModal('quoting-migration');
      } else {
        mutate({ key: 'quotingEnabled', value: false } as Setting);
      }
    },
  };

  const staffOnlyHelp = !isSparkStaff ? (
    <span className="flex items-center">
      <Trans
        ns="features"
        i18nKey="configurations.components.setting_toggle_row.staff_only_help"
        components={{
          contact: (
            <button
              type="button"
              className={cn(
                'inline-flex cursor-pointer items-center font-medium text-indigo-600 hover:text-indigo-500',
                INTERCOM_TRIGGER.inventoryFeature,
              )}
            />
          ),
        }}
      />
    </span>
  ) : undefined;

  return (
    <Card>
      <Card.Header headingLevel="h2">{t(`${I18N_PAGE}.cards.features.title`)}</Card.Header>
      <Card.Body>
        <div className="divide-y divide-gray-200">
          <SettingRow>
            <SettingToggleRow
              label={t(`${FIELDS}.company_users.label`)}
              description={t(`${FIELDS}.company_users.description`)}
              checked={companyUsers.checked}
              onChange={companyUsers.onChange}
              learnMoreUrl={DOCS_URLS.companyUsers}
            />
          </SettingRow>

          {quotingFeatureEnabled && (
            <SettingRow>
              <SettingToggleRow
                label={t(`${FIELDS}.quoting.label`)}
                description={t(`${FIELDS}.quoting.description`)}
                checked={quotingToggle.checked}
                onChange={quotingToggle.onChange}
                learnMoreUrl={DOCS_URLS.quoting}
                replacement={
                  quotingUpsell ? (
                    <PlanUpgradePrompt
                      availabilityLabel={t(`${COMPONENTS}.plan_upgrade_prompt.available_on_pro`)}
                      changePlanLabel={t(`${COMPONENTS}.plan_upgrade_prompt.change_plan`)}
                      trackingLabel="Quoting"
                    />
                  ) : undefined
                }
              />
            </SettingRow>
          )}

          {quotingFeatureEnabled && quotingEnabled && (
            <SettingRow nested>
              <SettingActionRow
                label={t(`${FIELDS}.quote_statuses.label`)}
                description={t(`${FIELDS}.quote_statuses.description`)}
                learnMoreUrl={DOCS_URLS.quoteStatuses}
                actionLabel={t(`${COMPONENTS}.setting_action_row.edit`)}
                onAction={() => openVueModal('quote-statuses')}
              />
            </SettingRow>
          )}

          <SettingRow>
            <SettingToggleRow
              label={t(`${FIELDS}.purchase_timeline.label`)}
              description={t(`${FIELDS}.purchase_timeline.description`)}
              checked={purchaseTimeline.checked}
              onChange={purchaseTimeline.onChange}
              learnMoreUrl={DOCS_URLS.purchaseTimeline}
            />
          </SettingRow>

          {notificationsActive && (
            <SettingRow nested>
              <SettingActionRow
                label={t(`${FIELDS}.purchase_notifications.label`)}
                description={t(`${FIELDS}.purchase_notifications.description`)}
                actionLabel={t(`${COMPONENTS}.setting_action_row.edit`)}
                onAction={() => openVueModal('purchase-notifications')}
              />
            </SettingRow>
          )}

          <SettingRow>
            <SettingToggleRow
              label={t(`${FIELDS}.invoicing.label`)}
              description={t(`${FIELDS}.invoicing.description`)}
              checked={invoicing.checked}
              onChange={invoicing.onChange}
              learnMoreUrl={DOCS_URLS.invoices}
              replacement={
                invoicingRestricted ? (
                  <PlanUpgradePrompt
                    availabilityLabel={t(`${COMPONENTS}.plan_upgrade_prompt.available_on_growth`)}
                    changePlanLabel={t(`${COMPONENTS}.plan_upgrade_prompt.change_plan`)}
                    trackingLabel="Invoicing"
                  />
                ) : undefined
              }
            />
          </SettingRow>

          {invoicingEnabled && (
            <SettingRow nested>
              <SettingActionRow
                label={t(`${FIELDS}.invoice_settings.label`)}
                description={t(`${FIELDS}.invoice_settings.description`)}
                learnMoreUrl={DOCS_URLS.invoices}
                actionLabel={t(`${COMPONENTS}.setting_action_row.edit`)}
                onAction={() => openVueModal('invoicing-settings')}
              />
            </SettingRow>
          )}

          <SettingRow>
            <SettingToggleRow
              label={t(`${FIELDS}.multi_location_stock.label`)}
              description={t(`${FIELDS}.multi_location_stock.description`)}
              checked={multiLocationStock.checked}
              onChange={multiLocationStock.onChange}
              learnMoreUrl={DOCS_URLS.multiLocationStock}
              disabled={!isSparkStaff}
              helpText={staffOnlyHelp}
            />
          </SettingRow>

          {paymentsVisibilityRow && (
            <SettingRow>
              <SettingToggleRow
                label={t(`${FIELDS}.payments_visibility.label`)}
                description={t(`${FIELDS}.payments_visibility.description`)}
                checked={paymentsVisibility.checked}
                onChange={paymentsVisibility.onChange}
                learnMoreUrl={DOCS_URLS.paymentsVisibility}
              />
            </SettingRow>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
